// Franchise Owners: the clinic roster.
//
// Everyone signed in reads it: clinic, territory, and whether the clinic is
// opted in for National Account onsites. Owners get their own clinics pinned
// at the top, where they edit everything except the franchise ID and the
// legal entity; the opt-in state moves only through the header button (or an
// admin override here). Admin keeps the full roster: add, edit, import,
// opt-in override and removal.
//
// Activity records, onsites and Franchisee users all attribute to a row
// here, which is why removal is guarded on the server rather than only
// discouraged here.

import { useCallback, useEffect, useState } from 'react'
import {
  listFranchisees,
  createFranchisee,
  removeFranchisee,
  updateFranchisee,
  importFranchiseesFile,
} from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import { SEPARATOR } from '../vocab.js'
import {
  Button,
  EmptyState,
  Input,
  Notice,
  Panel,
  Pill,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { useConfirm } from '../confirm.jsx'
import ImportCard from '../ImportCard.jsx'

const EMPTY = {
  name: '',
  entityName: '',
  franchiseId: '',
  territory: '',
  homeBaseAddress: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  onsiteContactName: '',
  onsiteContactEmail: '',
  onsiteContactPhone: '',
  notes: '',
}

// The clinic: the franchise location itself, its identity and main contact.
const CLINIC_FIELDS = [
  ['name', 'Name'],
  ['entityName', 'Legal entity'],
  ['franchiseId', 'Franchise ID'],
  ['territory', 'Territory'],
  ['homeBaseAddress', 'Home base address'],
  ['contactName', 'Contact name'],
  ['contactEmail', 'Contact email'],
  ['contactPhone', 'Contact phone'],
]

// On-site: who to coordinate with when work happens at the customer's site.
const ONSITE_FIELDS = [
  ['onsiteContactName', 'On-site contact name'],
  ['onsiteContactEmail', 'On-site email'],
  ['onsiteContactPhone', 'On-site phone'],
]

// Owners edit everything except the franchise ID and the legal entity, which
// stay admin ground. The opt-in flag moves through the header button.
const ADMIN_ONLY_KEYS = new Set(['franchiseId', 'entityName'])
const OWNER_CLINIC_FIELDS = CLINIC_FIELDS.filter(([key]) => !ADMIN_ONLY_KEYS.has(key))

const EMAIL_KEYS = new Set(['contactEmail', 'onsiteContactEmail'])

const optTone = (isActive) => (isActive ? 'ok' : 'neutral')
const optLabel = (isActive) => (isActive ? 'Opted in' : 'Opted out')

// The body both clinic forms share: the clinic fields a role may edit, the
// on-site contact section, and notes.
function ClinicFormBody({ fields, form, setForm, fieldErrors }) {
  return (
    <>
      <FieldGrid fields={fields} form={form} setForm={setForm} fieldErrors={fieldErrors} />
      <div className="form-section">
        <h2>On-site information</h2>
        <p className="support">
          Who to coordinate with when work happens at the customer&apos;s site.
        </p>
      </div>
      <FieldGrid fields={ONSITE_FIELDS} form={form} setForm={setForm} fieldErrors={fieldErrors} />
      <div className="formgrid" style={{ marginTop: 16 }}>
        <Input
          id="f-notes"
          className="span-2"
          label="Notes"
          multiline
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />
      </div>
    </>
  )
}

function FieldGrid({ fields, form, setForm, fieldErrors }) {
  return (
    <div className="formgrid">
      {fields.map(([key, label]) => (
        <Input
          key={key}
          id={`f-${key}`}
          label={label}
          type={EMAIL_KEYS.has(key) ? 'email' : 'text'}
          value={form[key]}
          onChange={(event) => setForm({ ...form, [key]: event.target.value })}
          error={fieldErrors[key]}
          help={key === 'homeBaseAddress' ? 'Where mileage is measured from.' : undefined}
        />
      ))}
    </div>
  )
}

// One of the owner's own clinics: the full record, editable in place.
function MyClinicCard({ clinic, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  function startEdit() {
    setForm(
      Object.fromEntries(
        Object.keys(EMPTY)
          .filter((key) => !ADMIN_ONLY_KEYS.has(key))
          .map((key) => [key, clinic[key] ?? '']),
      ),
    )
    setEditing(true)
  }

  async function save(event) {
    event.preventDefault()
    const result = await submit(() => updateFranchisee(clinic.id, form))
    if (result) {
      setEditing(false)
      onSaved()
    }
  }

  const detailRows = [...CLINIC_FIELDS, ...ONSITE_FIELDS]

  return (
    <Panel
      title={clinic.name}
      support={
        [clinic.entityName, clinic.franchiseId].filter(Boolean).join(SEPARATOR) ||
        'No legal entity recorded'
      }
      actions={
        <>
          <Pill tone={optTone(clinic.isActive)}>{optLabel(clinic.isActive)}</Pill>{' '}
          {editing ? null : <Button onClick={startEdit}>Edit</Button>}
        </>
      }
    >
      {editing ? (
        <form onSubmit={save} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}
          <p className="support">
            The franchise ID and legal entity are set by National Accounts; ask us to correct those.
            Opt in or out of onsites with the button in the header.
          </p>
          <ClinicFormBody
            fields={OWNER_CLINIC_FIELDS}
            form={form}
            setForm={setForm}
            fieldErrors={fieldErrors}
          />
          <div className="btnrow">
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving' : 'Save changes'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="rows">
          {detailRows.map(([key, label]) => (
            <div key={key} className="row">
              <div className="row-main">
                <div className="row-meta">{label}</div>
                <div className="row-primary">
                  {clinic[key] || <span className="muted">Not set</span>}
                </div>
              </div>
            </div>
          ))}
          {clinic.notes ? (
            <div className="row">
              <div className="row-main">
                <div className="row-meta">Notes</div>
                <div className="row-primary wraps">{clinic.notes}</div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  )
}

export default function Franchisees() {
  const confirm = useConfirm()
  const { can } = useAuth()
  const canWrite = can(ACTION.FRANCHISEES_WRITE)
  const [franchisees, setFranchisees] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [rowError, setRowError] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [importing, setImporting] = useState(false)
  const { saving, error, fieldErrors, submit } = useApiForm()

  const load = useCallback(async () => {
    try {
      const data = await listFranchisees()
      setFranchisees(data.franchisees)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function startEdit(franchisee) {
    setEditing(franchisee)
    setForm(Object.fromEntries(Object.keys(EMPTY).map((key) => [key, franchisee[key] ?? ''])))
  }

  function stopEdit() {
    setEditing(null)
    setForm(EMPTY)
  }

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() =>
      editing ? updateFranchisee(editing.id, form) : createFranchisee(form),
    )
    if (result) {
      stopEdit()
      load()
    }
  }

  async function toggleActive(franchisee) {
    setRowError(null)
    try {
      await updateFranchisee(franchisee.id, { isActive: !franchisee.isActive })
      load()
    } catch (caught) {
      setRowError(caught.message)
    }
  }

  async function drop(franchisee) {
    if (!(await confirm(`Remove ${franchisee.name}?`))) return
    setRowError(null)
    try {
      await removeFranchisee(franchisee.id)
      if (editing?.id === franchisee.id) stopEdit()
      load()
    } catch (caught) {
      setRowError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!franchisees) return <div className="center-note">Loading</div>

  const mine = franchisees.filter((franchisee) => franchisee.mine)

  const roster = (
    <Panel
      title="Franchise Owners"
      support={`${franchisees.length} on file; opted-in clinics take National Account onsites`}
      flush
    >
      {franchisees.length === 0 ? (
        <EmptyState title="No franchise owners yet">
          Add the clinics that service National Accounts customers.
        </EmptyState>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Clinic</Th>
              <Th>Territory</Th>
              {canWrite ? <Th numeric>Customers served</Th> : null}
              <Th>Onsites</Th>
              {canWrite ? <Th numeric>Actions</Th> : null}
            </Tr>
          </Thead>
          <Tbody>
            {franchisees.map((franchisee) => (
              <Tr key={franchisee.id} className={franchisee.isActive ? undefined : 'is-dimmed'}>
                <Td>
                  <div className="row-primary">
                    {franchisee.name}
                    {franchisee.mine ? (
                      <>
                        {' '}
                        <Pill tone="neutral">Yours</Pill>
                      </>
                    ) : null}
                  </div>
                  {canWrite ? (
                    <div className="ui-cell-meta">
                      {[franchisee.entityName, franchisee.franchiseId]
                        .filter(Boolean)
                        .join(SEPARATOR) || 'No entity recorded'}
                    </div>
                  ) : null}
                </Td>
                <Td>{franchisee.territory || <span className="muted">Not set</span>}</Td>
                {canWrite ? (
                  <Td
                    numeric
                    title="Customers with a recorded service or a completed onsite by this clinic"
                  >
                    {franchisee.customersServed ?? 0}
                  </Td>
                ) : null}
                <Td>
                  <Pill tone={optTone(franchisee.isActive)}>{optLabel(franchisee.isActive)}</Pill>
                </Td>
                {canWrite ? (
                  <Td numeric>
                    <Button onClick={() => startEdit(franchisee)}>Edit</Button>{' '}
                    <Button onClick={() => toggleActive(franchisee)}>
                      {franchisee.isActive ? 'Opt out' : 'Opt in'}
                    </Button>{' '}
                    <Button variant="ghost" onClick={() => drop(franchisee)}>
                      Remove
                    </Button>
                  </Td>
                ) : null}
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Panel>
  )

  return (
    <>
      {rowError ? <Notice tone="bad">{rowError}</Notice> : null}

      {/* The owner's own clinics come first, so their record is at hand
          without scrolling the roster. */}
      {mine.length ? (
        <div className="stack">
          {mine.map((clinic) => (
            <MyClinicCard key={clinic.id} clinic={clinic} onSaved={load} />
          ))}
        </div>
      ) : null}

      {canWrite ? (
        <div className="toolbar">
          <span className="spacer" />
          <Button variant="ghost" onClick={() => setImporting((open) => !open)}>
            Import
          </Button>
        </div>
      ) : null}

      {importing && canWrite ? (
        <ImportCard
          id="franchisees-file"
          title="Import franchise owners"
          description="CSV or Excel. Columns: name, and optionally franchise_id, entity_name, territory, home_base_address, contact_name, contact_email, contact_phone, onsite_contact_name, onsite_contact_email, onsite_contact_phone and notes. A file with any bad row is refused whole, and a franchisee already on the roster is refused rather than overwritten."
          request={importFranchiseesFile}
          describe={(result) => `${result.franchisees.length} franchisees imported.`}
          onImported={load}
          onClose={() => setImporting(false)}
        />
      ) : null}

      {canWrite ? (
        <div className="split">
          {roster}

          <Panel
            title={editing ? `Edit ${editing.name}` : 'Add a franchise owner'}
            support={
              editing ? 'Changes apply to the roster; recorded work keeps its history.' : undefined
            }
          >
            <form onSubmit={onSubmit} noValidate>
              {error ? <Notice tone="bad">{error}</Notice> : null}

              <div className="form-section" style={{ marginTop: 0 }}>
                <h2>Clinic information</h2>
                <p className="support">The franchise location and its main contact.</p>
              </div>
              <ClinicFormBody
                fields={CLINIC_FIELDS}
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
              />

              <div className="btnrow">
                {editing ? (
                  <Button variant="ghost" onClick={stopEdit}>
                    Cancel
                  </Button>
                ) : null}
                <span className="spacer" />
                <Button type="submit" variant="primary" block disabled={saving}>
                  {saving ? 'Saving' : editing ? 'Save changes' : 'Add franchise owner'}
                </Button>
              </div>
            </form>
          </Panel>
        </div>
      ) : (
        roster
      )}
    </>
  )
}
